/*
    This form is used by borrowers to request a payoff statement.
    This version of the form simply creates a task for the lender, and it is up to them to populate the statement with the requested information
*/

import { useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import { useContext, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { OpenPayoffStatus, RequestPayoffStatementDocument } from '@willow/graphql-iso/src/portal';
import { addressToString, getBorrowerNameWithoutMiddleName } from '@willow/shared-iso';
import { assertNever, Button, ControlledSingleDatePicker, WillowMultiSelect, z } from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { BorrowerId, PropertyId } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../App';
import { Alert } from '../alert/Alert';
import { UserContext } from '../context/UserContext';
import { WillowModal } from '../modal/Modal';
import { ManageLoanHeader } from './Header';

interface Props {
  loan: PortalSelectedLoan;
}

const PAYOFF_REQUEST_FORM_SHAPE = z.object({
  payoffDate: z.date(),
  additionalReceipients: BorrowerId.array(),
  // non-auto payoff fields
  propertyIds: z.array(PropertyId),
  message: z.string().optional(),
});
type PayoffRequestFormShape = z.infer<typeof PAYOFF_REQUEST_FORM_SHAPE>;

export const PayoffRequestForm = ({ loan }: Props) => {
  const [requestPayoffStatement] = useMutation(RequestPayoffStatementDocument);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const user = useContext(UserContext);

  const {
    formState: { errors, isSubmitting },
    register,
    control,
    handleSubmit,
    reset,
  } = useForm<PayoffRequestFormShape>({
    resolver: zodResolver(PAYOFF_REQUEST_FORM_SHAPE),
    defaultValues: {
      additionalReceipients: [],
      propertyIds: [loan.currentSnapshot.properties[0].id],
    },
  });

  // Property select options
  const propertyOptions = useMemo(
    () =>
      loan.currentSnapshot.properties
        .filter((p) => !p.isReleased)
        .map((p) => ({
          value: p.id,
          label: addressToString(p.address),
        })),
    [loan.currentSnapshot.properties],
  );

  // Filter out current borrower from list of additional recipient options
  const borrowers = [loan.primaryBorrower, ...loan.additionalBorrowers].filter((b) => !(b.id === user?.borrowerId));
  const recipientOptions = useMemo(
    () => [
      ...borrowers.map((b) => {
        return {
          value: b.id,
          label: <>{getBorrowerNameWithoutMiddleName(b)}</>,
        };
      }),
    ],
    [borrowers],
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      await requestPayoffStatement({
        variables: {
          loanId: loan.id,
          payoffDate: data.payoffDate,
          propertyIds: data.propertyIds,
          additionalBorrowers: data.additionalReceipients,
          message: data.message,
        },
      });
      setShowSuccessModal(true);
      reset();
    } catch (_err: unknown) {
      toast.error('Something went wrong submitting the payoff request form, please try again.');
    }
  });

  return (
    <>
      {loan.currentSnapshot.openPayoffStatus && !isSubmitting && !showSuccessModal && (
        <div className="mb-4">
          <Alert
            title=""
            subtitle={
              // eslint-disable-next-line react/no-unstable-nested-components -- TODO: pre-existing, surfaced by removing the NamedMemo wrapper; fix in follow-up
              (() => {
                switch (loan.currentSnapshot.openPayoffStatus) {
                  case OpenPayoffStatus.Requested:
                    return (
                      <>
                        <b>Payoff Requested:</b> Requesting a new payoff will cancel and replace currently active
                        request
                      </>
                    );
                  case OpenPayoffStatus.Active:
                    return (
                      <>
                        <b>Active Payoff:</b> Requesting a new payoff will cancel and replace currently active statement
                      </>
                    );
                  case OpenPayoffStatus.ActiveOutdated:
                    return <>A recent payment has been made; request a new payoff statement to ensure accuracy</>;
                  default:
                    assertNever(loan.currentSnapshot.openPayoffStatus);
                }
              })()
            }
            level="WARNING"
            variant="BOLD"
          />
        </div>
      )}

      <ManageLoanHeader
        title="Request a Payoff Statement"
        subtitle="Fill out the form below to request a payoff statement be emailed to you. "
      />

      <Form onSubmit={onSubmit}>
        <Form.Group className="mb-4">
          <Form.Label className="u-fs-2">
            <div className="u-bold">Select payoff date</div>
            <div className="u-color-bark3">This is the date you expect to pay your loan in full</div>
          </Form.Label>

          <ControlledSingleDatePicker
            fieldName="payoffDate"
            control={control}
            props={{
              minDate: DateTime.now().startOf('day').toJSDate(), // don't allow backdating
              maxDate: DateTime.now().startOf('day').plus({ days: 30 }).toJSDate(), // only allow up to 30 days for payoff expiration
            }}
          />
        </Form.Group>

        {/* Automatic payoff quote will be for a full-payoff. If not all multiple properties are selected, the task flow will ensue rather than auto-creating the payoff */}
        {loan.currentSnapshot.properties.length > 1 && (
          <Form.Group className="mb-4">
            <Form.Label className="u-fs-2 fw-bold">Select 1 or more properties to be paid off</Form.Label>
            <Controller
              name="propertyIds"
              control={control}
              render={({ field: { onChange, value } }) => (
                <WillowMultiSelect
                  placeholder="Select properties"
                  options={propertyOptions}
                  value={value ? propertyOptions.filter((r) => value.includes(r.value)) : []}
                  onChange={(properties) => {
                    onChange(properties ? properties.map((p) => p.value) : []);
                  }}
                  isClearable={false}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="propertyIds"
              render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
            />
          </Form.Group>
        )}

        {borrowers.length > 0 && (
          <Form.Group className="mb-4">
            <Form.Label className="u-fs-2">
              <div className="u-bold">Additional payoff quote recipient (optional)</div>
              <div className="u-color-bark3">Select authorized contact(s) to also receive a payoff quote</div>
            </Form.Label>
            <Controller
              name="additionalReceipients"
              control={control}
              render={({ field: { onChange, value } }) => (
                <WillowMultiSelect
                  placeholder="Select recipient(s)"
                  options={recipientOptions}
                  value={recipientOptions.filter((r) => value.includes(r.value))}
                  onChange={(notificiationRecipients) => {
                    onChange({
                      target: {
                        name: `recipients`,
                        value: notificiationRecipients
                          ? notificiationRecipients.map((recipient) => recipient.value)
                          : [],
                      },
                    });
                  }}
                />
              )}
            />
          </Form.Group>
        )}

        {/* If we are potentially generating an automated payoff, just hide the comment field as thats easier to deal with than edge cases to include it */}
        {!loan.currentSnapshot.isEligibleForAutomatedPayoff && (
          <Form.Group>
            <Form.Label className="u-fs-2 u-bold">Add additional notes to lender (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={7}
              className="w-100"
              {...register('message')}
              placeholder="Add your note here"
            />

            <ErrorMessage
              errors={errors}
              name="message"
              render={({ message }) => <div className="mt-1 u-color-red1 w-100">{message}</div>}
            />
          </Form.Group>
        )}

        <Button className="mt-3 align-self-right" variant="primary" size="md" onClick={onSubmit} loading={isSubmitting}>
          Submit Request
        </Button>
      </Form>

      <WillowModal showModal={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Alert title="Your payoff statement request has been submitted" level="INFO" />
        <div className="mt-3">
          {loan.company.name} is generating your payoff statement and you will be notified via email when it is ready
          for download from your Documents section.
        </div>
        <div className="mt-4 ms-auto">
          <Button
            variant="primary"
            size="sm"
            className="hover-override hover:u-bg-secondary"
            onClick={() => setShowSuccessModal(false)}
          >
            Close
          </Button>
        </div>
      </WillowModal>
    </>
  );
};
